import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaArrowRight,
    FaUnity,
    FaGamepad,
    FaLayerGroup,
} from "react-icons/fa";
import { scrollToElement } from "../../utils/scroll";

const ArrowRightIcon = FaArrowRight as unknown as React.FC<{ className?: string }>;
const UnityIcon = FaUnity as unknown as React.FC<{ className?: string }>;
const GamepadIcon = FaGamepad as unknown as React.FC<{ className?: string }>;
const LayerGroupIcon = FaLayerGroup as unknown as React.FC<{ className?: string }>;

type ProjectCard = {
    name: string;
    category: string;
    description: string;
    imageSrc: string;
    imageAlt: string;
    status: string;
};

const PROJECTS: ProjectCard[] = [
    {
        name: "Journey to Echo",
        category: "FPS Survival",
        description:
            "Bunker survival, vehicle repair, signal restoration and escape-focused progression.",
        imageSrc: "./journey2.png",
        imageAlt: "Journey to Echo",
        status: "Coming Soon",
    },
    {
        name: "City Delivery Simulator",
        category: "Mobile Simulation",
        description:
            "Route-based delivery gameplay with upgrades, progression and city-scale logistics.",
        imageSrc: "./citydelivery.png",
        imageAlt: "City Delivery Simulator",
        status: "Released",
    },
    {
        name: "Traffic Car",
        category: "Arcade Driving",
        description:
            "Fast traffic gameplay built around simple controls, feedback and short sessions.",
        imageSrc: "./trafficar.jpg",
        imageAlt: "Traffic Car",
        status: "Released",
    },
];

const STUDIO_CARDS = [
    {
        Icon: UnityIcon,
        title: "Unity Production",
        text: "Gameplay systems, UI, interactions and build structure.",
    },
    {
        Icon: GamepadIcon,
        title: "Gameplay First",
        text: "Every feature is shaped around feel, clarity and feedback.",
    },
    {
        Icon: LayerGroupIcon,
        title: "Visual Identity",
        text: "Screenshots, UI, worlds and store assets built as one direction.",
    },
] as const;

const NETWORK_NODES = [
    { label: "Bunker Survival", x: "58%", y: "18%" },
    { label: "Repair Vehicle", x: "62%", y: "50%" },
    { label: "Restore Tower", x: "58%", y: "77%" },
    { label: "Fight Zombies", x: "79%", y: "36%" },
    { label: "Escape Route", x: "79%", y: "63%" },
] as const;

const HeroComponent: React.FC = () => {
    const navigate = useNavigate();

    const handleViewProjects = useCallback(() => {
        navigate("/games");
    }, [navigate]);

    const handleLearnMore = useCallback(() => {
        scrollToElement("about");
    }, []);

    return (
        <section
            id="hero"
            className="relative overflow-hidden bg-[#070706] text-[#F3E8D6]"
        >
            {/* BACKGROUND */}
            <div className="pointer-events-none absolute inset-0">
                {/*<img*/}
                {/*    src="./journeytoecho.jpg"*/}
                {/*    alt=""*/}
                {/*    className="absolute inset-0 h-full w-full object-cover opacity-[0.12]"*/}
                {/*/>*/}

                <div className="absolute inset-0 bg-gradient-to-b from-[#070706] via-[#0B0907]/92 to-[#070706]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_36%,rgba(198,160,106,0.18),transparent_32%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(232,221,202,0.08),transparent_28%)]" />

                <div
                    className="absolute inset-0 opacity-[0.055]"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, rgba(255,255,255,0.28) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.28) 1px, transparent 1px)",
                        backgroundSize: "58px 58px",
                    }}
                />
            </div>

            <div className="relative z-10 mx-auto max-w-[1500px] px-4 py-14 sm:px-6 sm:py-18 lg:px-10 lg:py-24">
                {/* MAIN HERO */}
                <div className="grid min-h-[620px] gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:items-center">
                    {/* LEFT TEXT */}
                    <div className="animate-[fadeUp_0.8s_ease_both]">
                        <div className="mb-7 flex items-center gap-3">
                            <span className="h-px w-12 bg-[#C6A06A]" />
                            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-[#C6A06A]">
                                Northruins Studio
                            </p>
                        </div>

                        <h1 className="max-w-[760px] text-[48px] font-black uppercase leading-[0.88] tracking-[-0.055em] text-[#F4E8D4] sm:text-[72px] lg:text-[88px]">
                            Build worlds.
                            <br />
                            <span className="text-[#B48B57]">Survive the signal.</span>
                        </h1>

                        <p className="mt-7 max-w-[560px] text-[15px] leading-8 text-[#CDBFAE] sm:text-[16px]">
                            Atmospheric survival games focused on exploration, repair,
                            tension and clear gameplay systems.
                        </p>

                        <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                            <button
                                type="button"
                                onClick={handleViewProjects}
                                className="group inline-flex items-center justify-center gap-3 bg-[#E8DDCA] px-6 py-3.5 text-[12px] font-black uppercase tracking-[0.14em] text-[#11100D] transition duration-300 hover:-translate-y-1 hover:bg-white"
                            >
                                View Projects
                                <ArrowRightIcon className="h-3.5 w-3.5 transition group-hover:translate-x-1" />
                            </button>

                            <button
                                type="button"
                                onClick={handleLearnMore}
                                className="inline-flex items-center justify-center border border-white/10 bg-white/[0.03] px-6 py-3.5 text-[12px] font-black uppercase tracking-[0.14em] text-[#EADBC3] transition duration-300 hover:-translate-y-1 hover:bg-white/[0.07]"
                            >
                                About Studio
                            </button>
                        </div>
                    </div>

                    {/* RIGHT NETWORK */}
                    <div className="relative min-h-[520px] animate-[fadeUp_1s_ease_both] overflow-hidden border border-[#302A21] bg-[#0D0C09]/85 shadow-[0_30px_90px_rgba(0,0,0,0.35)] backdrop-blur-sm">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(198,160,106,0.16),transparent_38%)]" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />

                        {/* faint UI mock */}
                        <div className="absolute left-6 top-24 hidden w-[260px] border border-white/8 bg-black/25 p-5 opacity-45 blur-[0.2px] md:block">
                            <div className="mb-5 h-3 w-24 bg-white/10" />
                            <div className="space-y-3">
                                <div className="h-8 bg-white/8" />
                                <div className="h-8 bg-white/5" />
                                <div className="h-8 bg-white/5" />
                                <div className="h-8 bg-white/5" />
                            </div>
                        </div>

                        {/* SVG CONNECTION LINES */}
                        <svg
                            className="absolute inset-0 h-full w-full"
                            viewBox="0 0 900 560"
                            fill="none"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M260 280 C390 280 395 130 520 130"
                                stroke="url(#goldGlow)"
                                strokeWidth="3"
                                className="network-line"
                            />
                            <path
                                d="M260 280 C410 280 420 280 545 280"
                                stroke="url(#goldGlow)"
                                strokeWidth="3"
                                className="network-line delay-1"
                            />
                            <path
                                d="M260 280 C390 280 405 430 525 430"
                                stroke="url(#goldGlow)"
                                strokeWidth="3"
                                className="network-line delay-2"
                            />
                            <path
                                d="M545 280 C650 280 660 205 760 205"
                                stroke="url(#goldGlow)"
                                strokeWidth="3"
                                className="network-line delay-3"
                            />
                            <path
                                d="M545 280 C660 280 665 350 760 350"
                                stroke="url(#goldGlow)"
                                strokeWidth="3"
                                className="network-line delay-4"
                            />

                            <defs>
                                <linearGradient id="goldGlow" x1="0" y1="0" x2="1" y2="0">
                                    <stop stopColor="#E8DDCA" stopOpacity="0" />
                                    <stop offset="0.5" stopColor="#D2B48C" />
                                    <stop offset="1" stopColor="#E8DDCA" stopOpacity="0.2" />
                                </linearGradient>
                            </defs>
                        </svg>

                        {/* NODES */}
                        <div className="absolute left-[7%] top-1/2 -translate-y-1/2 border border-[#C6A06A]/40 bg-[#11100D]/90 px-5 py-4 shadow-[0_0_35px_rgba(198,160,106,0.12)] backdrop-blur-md">
                            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C6A06A]">
                                Core Game
                            </p>
                            <p className="mt-1 text-[18px] font-black uppercase text-[#F4E8D4]">
                                Journey to Echo
                            </p>
                        </div>

                        {NETWORK_NODES.map((node, index) => (
                            <div
                                key={node.label}
                                className="absolute border border-[#C6A06A]/35 bg-[#160F0E]/85 px-4 py-3 shadow-[0_0_35px_rgba(198,160,106,0.12)] backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:border-[#D2B48C]"
                                style={{
                                    left: node.x,
                                    top: node.y,
                                    transform: "translate(-50%, -50%)",
                                    animation: `floatNode 4s ease-in-out ${index * 0.25}s infinite`,
                                }}
                            >
                                <p className="whitespace-nowrap text-[11px] font-black uppercase tracking-[0.14em] text-[#F3E8D6]">
                                    {node.label}
                                </p>
                            </div>
                        ))}

                        {/* bottom label */}
                        <div className="absolute bottom-6 left-6 right-6 border border-white/10 bg-black/30 p-5 backdrop-blur-md">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#C6A06A]">
                                Upcoming FPS Survival
                            </p>
                            <p className="mt-2 max-w-[600px] text-[13px] leading-6 text-[#D8CBB8]">
                                A compact survival loop built around tension, resources, repair
                                systems and world progression.
                            </p>
                        </div>
                    </div>
                </div>

                {/* STUDIO CARDS */}
                <div className="mt-8 grid gap-4 md:grid-cols-3">
                    {STUDIO_CARDS.map((item, index) => {
                        const Icon = item.Icon;

                        return (
                            <div
                                key={item.title}
                                className="group border border-[#302A21] bg-[#171511] p-6 transition duration-300 hover:-translate-y-1 hover:border-[#C6A06A]/60"
                                style={{ animation: `fadeUp 0.75s ease ${index * 0.12}s both` }}
                            >
                                <div className="mb-5 flex h-11 w-11 items-center justify-center border border-[#3A3328] bg-[#211C16] text-[#C6A06A]">
                                    <Icon className="h-5 w-5 transition group-hover:scale-110" />
                                </div>

                                <p className="text-[12px] font-black uppercase tracking-[0.16em] text-[#E8DCC8]">
                                    {item.title}
                                </p>

                                <p className="mt-3 text-[13px] leading-7 text-[#CFC1AE]">
                                    {item.text}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* PROJECTS */}
                <div className="mt-16">
                    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="h-px w-12 bg-[#C6A06A]" />
                                <p className="text-[12px] font-black uppercase tracking-[0.2em] text-[#C6A06A]">
                                    Selected Projects
                                </p>
                            </div>

                            <h3 className="mt-3 text-[30px] font-black uppercase leading-none tracking-[-0.035em] text-[#F1E5D3] sm:text-[44px]">
                                Built around atmosphere and feel.
                            </h3>
                        </div>

                        <button
                            type="button"
                            onClick={handleViewProjects}
                            className="group inline-flex w-fit items-center gap-2 text-[11px] font-black uppercase tracking-[0.15em] text-[#D8CBB8] transition hover:text-[#F3E8D6]"
                        >
                            View all games
                            <ArrowRightIcon className="h-3 w-3 transition group-hover:translate-x-1" />
                        </button>
                    </div>

                    <div className="grid gap-5 lg:grid-cols-3">
                        {PROJECTS.map((project, index) => (
                            <article
                                key={project.name}
                                className="group overflow-hidden border border-[#302A21] bg-[#171511] transition duration-300 hover:-translate-y-1 hover:border-[#C6A06A]/50"
                                style={{ animation: `fadeUp 0.75s ease ${index * 0.1}s both` }}
                            >
                                <div className="relative overflow-hidden">
                                    <img
                                        src={project.imageSrc}
                                        alt={project.imageAlt}
                                        loading="lazy"
                                        className="aspect-[16/10] w-full object-cover transition duration-700 group-hover:scale-[1.04]"
                                    />

                                    <div className="absolute inset-0 bg-gradient-to-t from-black/78 via-black/15 to-transparent" />

                                    <div className="absolute left-4 top-4 border border-white/10 bg-black/40 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-[#F1E5D3] backdrop-blur-md">
                                        {project.status}
                                    </div>

                                    <div className="absolute bottom-4 left-4 right-4">
                                        <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#C6A06A]">
                                            {project.category}
                                        </p>
                                        <p className="mt-1 text-[24px] font-black uppercase text-[#F1E5D3]">
                                            {project.name}
                                        </p>
                                    </div>
                                </div>

                                <div className="p-5">
                                    <p className="text-[14px] leading-7 text-[#CFC1AE]">
                                        {project.description}
                                    </p>

                                    <button
                                        type="button"
                                        onClick={handleViewProjects}
                                        className="group/button mt-5 inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.14em] text-[#E8DCC8] transition hover:text-[#F3E8D6]"
                                    >
                                        View project
                                        <ArrowRightIcon className="h-3 w-3 transition group-hover/button:translate-x-1" />
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default React.memo(HeroComponent);